/*
 *  eq.ts - foundation of EQIT typescript implementation.
 *
 *	This is the basic module for EQIT's typescript modules.
 *
 *	Copyright (C) 2022-2024, EQIT. All rights reserved.
 *	Created by David Hsieh <david@eqit.com.tw>
 */
import { E_LOGLEVEL } from './types';
import { appendCSS, getMyDateTime, getSessionId, handleSessionTimeout, nuError, nuYesno, waitMask, waitProgress, waitUnmask, getLogger, setLoggerLevel, registerUserInfoGetter, nuNoperm } from './globals';
export const PARENT_INSTANCE = 'parentInstance';
export const SELF_INSTANCE = 'selfInstance';
const IDP_LOGOUT = EQI18N("H", "Do you want to logout from IDP too?");
/**
 *	EQIT TypeScript abstract class for any class.
 */
export class eqObject {
    constructor() {
        // eqlogger Log Level.
        // The value here is the default log level for all objects.
        this.logLevel = E_LOGLEVEL.NONE;
        // Return the class name of this instance.
        //get className()  { return this.constructor.name; }
    }
    /**
     * Return logger (qqlogger) of this instance.
     */
    get logger() {
        let nam = this.constructor.name;
        return this.logLevel ? setLoggerLevel(this.logLevel, nam) : getLogger(nam);
    }
    /** The class name of 'this' */
    get $className() { return this.constructor.name; }
}
////////////////////////////////////////////////////////////
//
//	Options-aware Object.
//	Options-aware Object with Context.
//
////////////////////////////////////////////////////////////
export class optObject extends eqObject {
    constructor(options) {
        super();
        // initialize the options
        this._options = options || null;
        // async calling init()
        let dfd = $.Deferred();
        setTimeout(() => {
            $.when(this.init())
                .done((d) => { dfd.resolve(d); })
                .fail(() => { dfd.reject(); });
        }, 0);
        this.ready = dfd.promise();
    }
    /**
     * DO NOT overload the constructor, use init() for the initialization.
     * The init() method will be called asynchronously and the class member
     * 'ready' (JQueryPromise) should be used to wait for the initialization.
     */
    init() {
        return true;
    }
}
export class comObject extends optObject {
    constructor(context, options) {
        super(options);
        // initialize the context
        this._anchor = context;
        this.ctx().data(SELF_INSTANCE, this);
    }
    /**
     * JQuery CSS selector to select HTML elements under the object's context.
     * @param selector JQuery CSS selector
     * @returns JQuery object
     */
    ctx(selector) {
        return selector === undefined ? this._anchor : $(selector, this._anchor);
    }
    /**
     * Set the parent instance. The instance should be
     * a subclass of eqObject
     * @param parent the instance of the parent.
     */
    setParentInstance(parent) {
        this.ctx().data(PARENT_INSTANCE, parent);
    }
    /**
     * Get the parent instance. The instance should be
     * a subclass of eqObject
     * @returns the instance of the parent,
     *		or undefined if not available.
     */
    getParentInstance() {
        return this.ctx().data(PARENT_INSTANCE);
    }
    /**
     * Append the styles at runtime.
     * @param id An unique ID of the styles. It will be used to avoid overloadding.
     * @param css One or more text of the styles.
     */
    AppendCSS(id, css) {
        appendCSS(id, css);
    }
}
////////////////////////////////////////////////////////////
//
//	EQAPI control components.
//
////////////////////////////////////////////////////////////
/**
 * EQAPI search object
 */
export class Search {
    constructor() {
        ////////////////////////////////////////////////////////
        //
        //	query value
        //
        ////////////////////////////////////////////////////////
        this._search = null;
    }
    get hasValue() { return this._search !== null; }
    get value() { return this._search; }
    /**
     * Reset/Clear the search.
     */
    reset() { this._search = null; return this; }
    /**
     * Set the search value.
     * @param v the search value
     * @returns this instance
     */
    set(v) { this._search = v; return this; }
}
/**
 * EQAPI filter object
 */
export class Filter {
    constructor() {
        ////////////////////////////////////////////////////////
        //
        //	query value
        //
        ////////////////////////////////////////////////////////
        this._filters = {};
    }
    get hasValue() { return $.isEmptyObject(this._filters) ? false : true; }
    get value() {
        let ret = [], dat = this._filters;
        for (let i in dat)
            ret.push(dat[i]);
        return ret;
    }
    /**
     * Reset/Clear the filter.
     */
    reset() { this._filters = {}; return this; }
    /**
     * set1 expects the value(s) contains 1 entry which is the value and the comparison is EQUAL.
     * set1('column1', 'TEXT', ['foo', 'bar'])		<- column1 EQUAL foo or bar
     * set1('column2', 'NUMBER', [1.2, 3.4], True)	<- column2 NOT-EQUAL 1.2 and 3.4
     *
     * @param field the column name
     * @param typ the data type
     * @param v the value(s)
     * @param exc exclude flag
     * @returns this instance
     */
    set1(field, typ, v, exc = false) {
        this._filters[field] = {
            field: field,
            type: typ,
            exclude: exc,
            values: v.map(i => ({ comparison: 'EQ', value: i }))
        };
        return this;
    }
    /**
     * set2 expects the value(s) contains 2 entries, one is comparison and the other is value.
     * set2('column1', 'TEXT', [['EQ','foo'], ['EQ','bar']]) <- column1 EQUAL foo or bar
     * set2('column2', 'NUMBER', [['LT',1.2], ['GT',3.4]])   <- column2 LESS-THAN 1.2 or GREATER-THAN 3.4
     *
     * @param field the column name
     * @param typ the data type
     * @param v the value(s)
     * @param exc exclude flag
     * @returns this instance
     */
    set2(field, typ, v, exc = false) {
        this._filters[field] = {
            field: field,
            type: typ,
            exclude: exc,
            values: v.map(i => ({ comparison: i[0], value: i[1] }))
        };
        return this;
    }
}
/**
 * EQAPI simple filter object
 */
export class Sfilter {
    constructor() {
        ////////////////////////////////////////////////////////
        //
        //	query value
        //
        ////////////////////////////////////////////////////////
        this._sfilter = {};
    }
    get hasValue() { return $.isEmptyObject(this._sfilter) ? false : true; }
    get value() { return this._sfilter; }
    /**
     * Query a filter of a specific field.
     * @param field the field name.
     * @returns The filter of the specific field.
     */
    filterOf(field) { return this._sfilter[field]; }
    /**
     * Reset/Clear the filter(s).
     * @param field the field name.
     * @returns this instance
     */
    reset(field) {
        if (field === undefined)
            this._sfilter = {};
        else if (this._sfilter[field] !== undefined)
            delete this._sfilter[field];
        return this;
    }
    /**
     * Set a simple filter.
     * @param field The field for this simple filter
     * @param typ The simple filter type.
     * @param val The value of the simple filter.
     * @returns this instance
     */
    set(field, typ, val) {
        // Set empty filter as NULL
        if (val === undefined || (Array.isArray(val) && val.length === 0))
            val = null;
        this._sfilter[field] = [typ, val];
        return this;
    }
}
/**
 * EQAPI sorter object
 */
export class Sorter {
    constructor() {
        ////////////////////////////////////////////////////////
        //
        //	query value
        //
        ////////////////////////////////////////////////////////
        this._sorter = [];
    }
    get hasValue() { return this._sorter.length > 0; }
    get value() { return this._sorter; }
    /**
     * Reset/Clear the sorter
     * @returns the sorter
     */
    reset() { this._sorter = []; return this; }
    /**
     * The 'sort' in set should be something like:
     *
     *	[
     *		['column1','ASC'],
     *		['column2','DESC'],
     *		...
     *	]
     *
     * @param sort the sorters
     * @returns this instance
     */
    set(sort) {
        this._sorter = sort.map(v => ({ field: v[0], direction: v[1] }));
        return this;
    }
    /**
     * Add a 'sorter' for a specific field/column.
     *
     * @param field the field/column name
     * @param direction Sort order
     * @returns this instance
     */
    add(field, direction) {
        this._sorter.push({ 'field': field, 'direction': direction });
        return this;
    }
}
/**
 * EQAPI pager object
 */
export class Pager {
    constructor() {
        ////////////////////////////////////////////////////////
        //
        //	query value
        //
        ////////////////////////////////////////////////////////
        this._pager = {};
    }
    get value() { return this._pager; }
    // offset
    get offset() { return this._pager['offset'] || 0; }
    set offset(val) {
        if (val === null)
            delete this._pager['offset'];
        else
            this._pager['offset'] = val;
    }
    // limit
    get limit() { return this._pager['limit'] || 0; }
    set limit(val) {
        if (val === null)
            delete this._pager['limit'];
        else
            this._pager['limit'] = val;
    }
    // fields
    get fields() { return this._pager['fields'] || null; }
    set fields(val) {
        if (val === null)
            delete this._pager['fields'];
        else
            this._pager['fields'] = val;
    }
}
export class FileKeeper extends optObject {
    constructor() {
        super(...arguments);
        this.__submitting_file = '';
    }
    get options() { return this._options || { waitMask: false }; }
    /**
     * JQuery CSS selector to select HTML elements under the object's context.
     * @param selector JQuery CSS selector
     * @returns JQuery object
     */
    ctx(selector) {
        return selector === undefined ? this._anchor : $(selector, this._anchor);
    }
    upload() {
        // remove any existing.
        $('.eq-filekeeper-uploader').remove();
        let deferred = $.Deferred();
        this._anchor = $('<div class="eq-filekeeper-uploader"></div>').appendTo('body');
        // setup upload form.
        this.ctx().append('<form method="post" enctype="multipart/form-data">' +
            '<input type="file" name="fk_upload_file"/>' +
            '</form>');
        this.ctx('form').css({ position: 'fixed', left: '-1000px', top: '-1000px' });
        let inputDom = this.ctx('form input');
        inputDom.on('change', () => {
            // show wait mask or not ?
            if (this.options.waitMask) {
                if (typeof this.options.waitMask !== 'string')
                    waitMask(null, this.options.waitProgress);
                else
                    waitMask(this.options.waitMask, this.options.waitProgress);
            }
            // Automatically submit the form when file is selected
            let formdata = new FormData(), xhr = new XMLHttpRequest(), file = inputDom[0], f = file.files ? file.files[0] : '';
            if (f instanceof File)
                this.__submitting_file = f.name;
            this.logger.debug(`${this.$className}: submitting file ${this.__submitting_file}`);
            formdata.append('fk_upload_file', f);
            xhr.upload.addEventListener('progress', e => {
                return this.progressHandler(e);
            }, false);
            xhr.addEventListener('load', e => {
                return this.completeHandler(e, deferred);
            }, false);
            xhr.addEventListener('error', e => {
                return this.errorHandler(e, deferred);
            }, false);
            xhr.addEventListener('abort', e => {
                return this.abortHandler(e, deferred);
            }, false);
            xhr.open('POST', '/cgi-bin/filekeeper');
            xhr.send(formdata);
        });
        this.ctx('form input').trigger('click');
        return deferred.promise();
    }
    ////////////////////////////////////////////////////////
    completeHandler(event, deferred) {
        if (!this.__submitting_file)
            return;
        try {
            let target = event.target, resp = JSON.parse(target.responseText);
            this.logger.debug(`${this.$className}::completeHandler: response of (${this.__submitting_file})`, resp);
            if (resp.response.result === 'GOOD')
                deferred.resolve(resp);
            else
                deferred.reject(resp.response.result, resp);
        }
        catch (err) {
            deferred.reject('INTERR', { 'response': { 'result': 'INTERR', 'message': err } });
        }
        if (this.options.waitMask)
            waitUnmask();
        this.ctx().remove();
    }
    progressHandler(event) {
        let progress = Math.round((event.loaded / event.total) * 100);
        this.logger.debug(`Progress - ${progress}%`);
        waitProgress(progress);
    }
    abortHandler(event, deferred) {
        deferred.reject('INTERR', { 'response': { 'result': 'ABORT', 'message': event } });
        this.ctx().remove();
    }
    errorHandler(event, deferred) {
        deferred.reject('INTERR', { 'response': { 'result': 'ERROR', 'message': event } });
        this.ctx().remove();
    }
    /*
    *	FileKeeper downloader
    *	Download file(s) by 'fid', where the 'fid'
    *	can be a string or an array with strings.
    */
    static fkDownload(fid) {
        let res = false, logger = getLogger('FileKeeper.fkDownload');
        if (!fid) {
            logger.warning('Unable to download from FileKeeper, no fid.');
        }
        else if (typeof fid === 'string') {
            window.location.href = `/cgi-bin/filekeeper?f=${fid}`;
            res = true;
        }
        else if (Array.isArray(fid)) {
            if (fid.length > 0) {
                $.each(fid, (_, f) => { window.location.href = `/cgi-bin/filekeeper?f=${f}`; });
                res = true;
            }
            else {
                logger.warning('No file to download', fid);
                res = false;
            }
        }
        else {
            logger.warning('Unable to download from FileKeeper, invalid fid', fid);
        }
        return res;
    }
}
/**
 * webSock is a singleton class.
 * Always use the property method - Instance,
 * to get the webSock instance.
 */
export class webSock extends eqObject {
    static get Instance() {
        if (!webSock._instance)
            webSock._instance = new webSock();
        return webSock._instance;
    }
    constructor() {
        super();
        this._seqno = 0;
        this._pending = {};
        this._connected = null;
        this._subscribed = {};
        this._subscription = {};
        this._cbfn = {};
        this._sessid = getSessionId();
    }
    bind(event, cbfn) {
        if (this._cbfn[event] === undefined)
            this._cbfn[event] = [cbfn];
        else if (this._cbfn[event].indexOf(cbfn) < 0)
            this._cbfn[event].push(cbfn);
    }
    /**
     * Connect to the server
     * @returns a promise that resolve a WebSocket object.
     */
    connect() {
        // return the promise if connected.
        if (this._connected)
            return this._connected;
        let inbuf = '', dfd = $.Deferred(), proto = location.protocol === 'https:' ? 'wss:' : 'ws:', socket = new WebSocket(`${proto}//${location.host}/eqws`), incoming = (e) => {
            // The terminating tag of a message is '\r\n\r\n',
            // A message might come in pieces, so we need to
            // gather the incoming and deliver a whole message.
            if (e)
                inbuf += e.data;
            let end = inbuf.indexOf('\r\n\r\n');
            if (end < 0)
                return null;
            let msg = inbuf.slice(0, end);
            inbuf = inbuf.slice(end + 4); // we don't need the end tag.
            return msg;
        }, split = (s) => {
            // Split name and value which seperated by colon.
            // str.replace('/^\s+/g', ''); -> is used to remove any leading whitespaces.
            if (!s)
                return null;
            let i = s.indexOf(':');
            return i > 0 ? [s.slice(0, i), s.slice(i + 1).replace('/^\s+/g', '')] : null;
        }, parse = (m) => {
            // Parse a message into an object.
            if (!m)
                return null;
            let ret = {};
            m.split('\r\n').forEach((v) => {
                let nv = split(v);
                if (nv)
                    ret[nv[0]] = nv[1];
            });
            return ret;
        };
        ////////////////////////////////////////////////////
        // Initialize the callback functions.
        // Some functions will be replaced after authentication.
        ////////////////////////////////////////////////////
        socket.onopen = e => {
            // The initial onOpen callback function.
            // The first request should be AUTH.
            this.logger.debug('webSock.onOpen (AUTH)', e);
            // initialization
            this._seqno = 0; // reset sequence ID.
            this._pending = {}; // for holding the pending requests.
            // Authenticate first
            socket.send([
                `EQWS-IDENTITY:${this._seqno}`,
                'EQWS-REQUEST:AUTH',
                `EQWS-PAYLOAD:${this._sessid}`
            ].join('\r\n') + '\r\n\r\n');
            this.logger.debug('webSock.onOpen - sending AUTH request');
            this._seqno += 1; // advance the sequence ID.
        };
        socket.onmessage = e => {
            // The initial onmessage callback function
            // We handle the response of our first AUTH request.
            let auth = parse(incoming(e));
            if (!auth) {
                // We got something, but not the AUTH response
                // that we are waiting for.
                this.logger.warning('webSock.onMessage - expect AUTH response but something else', e);
                return;
            }
            this.logger.debug('webSock.onMessage - Got AUTH response');
            if (auth['EQWS-RESPONSE'] !== 'GOOD') {
                this.logger.error('webSock.onMessage - Authentication Error', auth);
                return;
            }
            // replace the event handlers to normal version
            socket.onclose = e => {
                this.logger.warning('webSock.onClose - Disconnected', e);
                this._onclose();
                if (this._cbfn['DISCONNECTED'] !== undefined)
                    this._cbfn['DISCONNECTED'].forEach(cb => { cb(); });
                dfd.reject(e);
            };
            socket.onerror = e => {
                this.logger.error('webSock.onError', e);
                dfd.reject(e);
            };
            socket.onmessage = e => {
                let msg = parse(incoming(e));
                while (msg) {
                    this._onmessage(msg);
                    msg = parse(incoming(null));
                }
            };
            // socket connection is ready.
            dfd.resolve(socket);
            this.logger.info('webSock.onMessage - Connected');
            $.each(this._subscription, (k, v) => {
                this.logger.debug(`webSock: resubscribe ${k}, ${v.length} subscribers`);
                v.forEach((subs) => { subs.subscribe(); });
            });
            this._subscription = {};
            if (this._cbfn['CONNECTED'] !== undefined)
                this._cbfn['CONNECTED'].forEach(cb => { cb(); });
        };
        socket.onclose = e => {
            this.logger.error('webSock.onClose - INIT', e);
            this._onclose();
            dfd.reject(e);
        };
        socket.onerror = e => {
            this.logger.error('webSock.onError - INIT', e);
            dfd.reject(e);
        };
        this._connected = dfd.promise();
        return this._connected;
    }
    /**
     * Close the connection.
     * @returns a promise that resolve void
     */
    close() {
        let dfd = $.Deferred();
        if (!this._connected) {
            // No connection, resolve immediately
            dfd.resolve();
        }
        else {
            // Get the WebSocket object and close the connection.
            $.when(this._connected)
                .done((socket) => {
                socket.close();
            })
                .always(() => {
                this._connected = null;
                dfd.resolve();
            });
        }
        return dfd.promise();
    }
    /**
     * Reconnect the WebSocket
     * @param retry retry period in seconds.
     * @returns a promise the resolve a WebSocket object.
     */
    async reconnect(retry) {
        // DON'T close the existing connection
        // in retry mode.
        this.logger.debug(`${this.$className}.reconnect(${retry})`);
        if (!retry)
            await this.close();
        try {
            let s = await this.connect();
            this.logger.debug(`${this.$className}: reconnected`, s);
            return s;
        }
        catch (e) {
            this.logger.error(`${this.$className}: reconnect failed =>`, e);
            if (retry)
                setTimeout(() => { this.reconnect(retry); }, retry * 1000);
            throw e;
        }
    }
    /**
     * Send a EQIT websocket request.
     * @param req a request string.
     * @param payload a request payload
     * @returns a promise that resolve the response data.
     */
    request(req, payload) {
        let dfd = $.Deferred();
        $.when(this.connect())
            .done((socket) => {
            try {
                let msg = [
                    `EQWS-IDENTITY:${this._seqno}`,
                    `EQWS-REQUEST:${req}`,
                    `EQWS-PAYLOAD:${JSON.stringify(payload)}`
                ];
                socket.send(msg.join('\r\n') + '\r\n\r\n');
                // Save this request (dfd) in '_pending' list and
                // advance the seqno.
                this.logger.debug(`webSock.request - Send request #${this._seqno}`, msg);
                this._pending[this._seqno] = dfd;
                this._seqno += 1;
            }
            catch (e) {
                this.logger.warning(`webSock.request - failure: ${e}`, req, payload);
                dfd.reject('INTERR', e);
            }
        })
            .fail(() => {
            this.logger.warning('webSock.request - failure, connection not available.', req, payload);
            dfd.reject('INTERR', 'Request failure, No WebSocket Connection');
        });
        // The promise will be reject immediately if we encounter an error.
        // Otherwise, it will be resolve/reject when we get the response.
        return dfd.promise();
    }
    /**
     * Subscribe a notification.
     *
     * TODO: What do we need to return anything
     * for a subscription request ?
     * I don't have one so it return nothing now.
     *
     * @param subs an instance of wsSUBS.
     * @returns a promise that resolve void
     */
    subscribe(subs) {
        let dfd = $.Deferred();
        if (this._subscribed[subs.module_name] === undefined) {
            // This is a new subscription, send the request.
            this.logger.debug(`${this.$className}.subscribe(): ${subs.module_name} (NEW)`);
            $.when(this.request('SUBSCRIBE', subs.reqdata))
                .fail((r, e) => {
                this.logger.error(`${this.$className}.subscribe(): ${subs.module_name} - ${r}`, e);
                dfd.reject(`[SUBSCRIBE ERROR] ${r}`);
            })
                .done((d) => {
                this.logger.debug(`${this.$className}.subscribe(): ${subs.module_name} SUBSCRIBED`, d);
                this._subscribed[subs.module_name] = [subs];
                dfd.resolve();
            });
        }
        else if (this._subscribed[subs.module_name].indexOf(subs) < 0) {
            this.logger.debug(`${this.$className}.subscribe(): ${subs.module_name} +1`);
            this._subscribed[subs.module_name].push(subs);
            dfd.resolve();
        }
        else {
            // This is a programing error. Please check your code.
            // I_wsSubs instance should only call 'subscribe()' once.
            this.logger.warning(`Already subscribed (${subs.module_name})`, subs);
            dfd.resolve();
        }
        return dfd.promise();
    }
    unsubscribe(subs) {
        let dfd = $.Deferred(), _subs = this._subscribed[subs.module_name];
        if (_subs === undefined) {
            // Not subscribed yet.
            this.logger.debug(`${this.$className}.unsubscribe(): not subscribed`, subs);
            dfd.resolve();
        }
        else {
            let i = _subs.indexOf(subs);
            if (i < 0) {
                // No subscription found.
                this.logger.debug(`${this.$className}.unsubscribe(): not found`, subs);
                dfd.resolve();
            }
            else {
                this.logger.debug(`${this.$className}.unsubscribe(): remove`, subs);
                _subs.splice(i, 1);
                if (_subs.length === 0) {
                    this.logger.debug(`${this.$className}.unsubscribe(): UNSUBSCRIBE`, subs);
                    delete this._subscribed[subs.module_name];
                    $.when(this.request('UNSUBSCRIBE', subs.reqdata))
                        .fail((r, e) => {
                        this.logger.error(`${this.$className}.unsubscribe(): ${r}`, e);
                        dfd.resolve();
                    })
                        .done((d) => {
                        this.logger.debug(`${this.$className}.unsubscribe(): DONE`, d);
                        dfd.resolve();
                    });
                }
            }
        }
        return dfd.promise();
    }
    ////////////////////////////////////////////////////////
    //
    //	Private Methods
    //
    ////////////////////////////////////////////////////////
    _onclose() {
        if (this._connected)
            this._connected = null;
        this._pending = {};
        this._subscription = this._subscribed;
        this._subscribed = {};
    }
    _onmessage(msg) {
        let payload = null, error = null, reqid = parseInt(msg['EQWS-IDENTITY']), req = msg['EQWS-REQUEST'] || null, res = msg['EQWS-RESPONSE'] || null;
        // Decode the payload, it should always be JSON encoded.
        if (msg['EQWS-PAYLOAD']) {
            let _pl = msg['EQWS-PAYLOAD'];
            try {
                payload = JSON.parse(_pl);
            }
            catch (e) {
                //error = {code:'EINVAL', msg:'EQWS-PAYLOAD', raw:_payload};
                this.logger.error(`webSock.onmessage - Invalid EQWS-PAYLOAD\n${_pl}`);
                payload = null;
            }
        }
        // Handle the NOTIFY request first
        if (req) {
            // The client (EQIT websocket client) should only
            // get an NOTIFY request from the server, since the
            // client is usually the one requesting.
            if (req === 'TIMEOUT')
                handleSessionTimeout();
            else if (req !== 'NOTIFY')
                this.logger.warning(`Unkown EQWS-REQUEST:${req}`, msg);
            else if (!payload)
                this.logger.debug('Ignore an empty payload notification.');
            else {
                this.logger.debug('Got NOTIFY', payload);
                let subs = this._subscribed, pl = payload;
                for (let k in pl)
                    if (subs[k] !== undefined)
                        subs[k].forEach(i => { i.notify(pl[k]); });
            }
        }
        else if (this._pending[reqid] !== undefined) {
            this.logger.debug(`webSock.onmessage - Got response #${reqid}`, msg);
            // Get the deferred object and release the reference.
            let dfd = this._pending[reqid];
            delete this._pending[reqid];
            if (msg['EQWS-ERROR']) {
                let _err = msg['EQWS-ERROR'], e = _err.split(':');
                error = { raw: _err };
                if (e.length > 1) {
                    error.code = e[0];
                    error.msg = e[1];
                }
                if (payload) {
                    error.payload = payload;
                }
            }
            switch (res) {
                case 'GOOD':
                    dfd.resolve(payload);
                    break;
                case 'BADREQ':
                    // Only BADREQ deliver T_ErrorMessage
                    dfd.reject('BADREQ', error);
                    break;
                case 'ERROR':
                    // Error Information is carried by 'payload'
                    dfd.reject('ERROR', payload);
                    break;
                case 'NOAUTH':
                    // No valid authentication
                    dfd.reject('NOAUTH', payload);
                    break;
                case 'TIMEOUT':
                    // Session Timeout
                    dfd.reject('TIMEOUT', payload);
                    break;
                case 'INTERR':
                    // Error Information is carried by 'payload'
                    dfd.reject('INTERR', payload);
                    this.logger.debug('webSock.onmessage - Internal Error', payload);
                    break;
                default:
                    // Error Information is carried by 'payload'
                    dfd.reject(res, payload);
                    this.logger.error('webSock.onmessage - Unexpected Response', payload, msg);
                    break;
            }
        }
        else {
            this.logger.warning(`webSock.onmessage - unexpected response #${reqid}`);
        }
    }
}
////////////////////////////////////////////////////////////
//
//	wsDAO/wsEQAPI
//
////////////////////////////////////////////////////////////
const INTERR_NOTICE = EQI18N("H", "An internal error occurred, please contact the technical support.");
const EPERM_MSG = EQI18N("H", "It is a global setting. As a group user, you have no privilege to change the global settings.");
const CONNERR_MSG = EQI18N("H", "Unable to connect to the server, please try again later.");
// Final Implementation of the error message parser.
export function parseErrmsg_Sfilter(msg, callback) {
    let m = msg.split('.'), name = '', r = null;
    if (m[0] === 'sfilter')
        name = m.slice(1).join('.');
    r = callback(name);
    if (r)
        return r;
    else
        return null;
}
export function parseErrmsg_LeadingIndex(msg, callback, postfix = true) {
    let m = msg.split('.'), lindex = '', tindex = '', name = '', r = null;
    if (!isNaN(Number(m[0]))) {
        if (m.length > 2) {
            if (!isNaN(Number(m[2]))) {
                // Editor for multiple edit with multi value may return msg like '0.macaddr.0', devides msg into '0-0' & 'macaddr'.
                lindex = m[0];
                tindex = m[2];
                name = m[1];
            }
            else {
                // Editor for multiple edit with multi value may return msg like '0.validity.begin', devides msg into '0' & 'validity.begin'.
                lindex = m[0];
                name = m.slice(1).join('.');
            }
        }
        else {
            // Editor for multiple edit may return msg like '0.macaddr', devides msg into '0' & 'macaddr'.
            lindex = m[0];
            name = m[1];
        }
    }
    r = callback(name, lindex, tindex);
    if (r && postfix === true) {
        let x = (nm) => r + `(#${nm})`;
        if (lindex && tindex)
            r = x(`${lindex}-${tindex}`);
        else if (lindex)
            r = x(lindex);
        else if (tindex)
            r = x(tindex);
    }
    return r;
}
export function parseErrMsg_TailingIndex(msg, callback, postfix = true) {
    let m = msg.split('.'), tindex = '', name = '', r = null;
    // multiple field vale may return msg like 'descr.0', devides msg into 'descr' & '0'.
    if (!isNaN(Number(m[1]))) {
        // just handle msg length=2 (e.g. 'descr.0'), otherwise handle by itself particularily.
        if (m.length = 2) {
            tindex = m[1];
            name = m[0];
        }
    }
    r = callback(name, tindex);
    if (r && postfix === true) {
        let x = (nm) => r + `(#${nm})`;
        if (tindex)
            r = x(tindex);
    }
    return r;
}
export class wsDAO_abstract extends eqObject {
    getCustom(name) {
        return this._customs ? this._customs[name] : null;
    }
    setCustom(name, value) {
        if (!this._customs)
            this._customs = {};
        this._customs[name] = value;
        return this;
    }
    setCustoms(req) {
        this._customs = req || null;
        return this;
    }
    constructor(opt) {
        super();
        this.eqSearch = new Search();
        this.eqFilter = new Filter();
        this.eqSfilter = new Sfilter();
        this.eqSorter = new Sorter();
        this.eqPager = new Pager();
        ////////////////////////////////////////////////////////
        //
        //	Custom Requests manipulations
        //
        ////////////////////////////////////////////////////////
        this._customs = null;
        if (opt)
            this._onErrMsg = opt.onErrMsg;
    }
    ////////////////////////////////////////////////////////
    //
    //	Sanitizer
    //
    ////////////////////////////////////////////////////////
    /**
     *
     * @param d
     * @returns
     */
    sanitizedForm(d) {
        if (d && d.payload && typeof d.payload === 'object' && !Array.isArray(d.payload))
            return d;
        this.logger.error(`${this.$className}: Invalid T_FormResponse`, d);
        return null;
    }
    sanitizedRecords(d) {
        if (d && d.payload) {
            if (Array.isArray(d.payload) && d.page)
                return d;
            // See if it is a response from a datapath API.
            if (d.datapath !== undefined) {
                // Yes, it's from datapath API. Convert the payload to T_RecordPayload
                let _payload = [];
                $.each(d.payload, (k, v) => {
                    v['@key'] = k;
                    _payload.push(v);
                });
                d.payload = _payload;
                return d;
            }
        }
        this.logger.error(`${this.$className}: Invalid T_RecordResponse`, d);
        return null;
    }
    ////////////////////////////////////////////////////////
    //
    //	Error Handling.
    //
    ////////////////////////////////////////////////////////
    /**
     * Hook a error message handling callback function.
     * This approach is DEPRECATED and use the option of onErrMsg instead.
     * @deprecated Use the 'onErrMsg' option of the construction instead.
     */
    set errorMessageCBFN(cbfn) {
        this._errorMessage = cbfn;
    }
    async handleError(r, e) {
        this.logger.debug(`${this.$className}: ${this.daoName} error [${r}] =>`, e);
        switch (r) {
            case 'BADREQ': return await this.handleBADREQ(e);
            case 'NOPERM':
                await nuNoperm();
                return null;
            case 'TIMEOUT':
                handleSessionTimeout();
                return null;
            case 'NOAUTH': return null;
            case 'CONNERR':
                await nuError(CONNERR_MSG);
                return null;
            default:
                await nuError(INTERR_NOTICE);
                return null;
        }
    }
    async handleBADREQ(e) {
        let ret = null, m = null;
        if (typeof e === 'string') {
            // The error message of some API is carried by T_ErrorMessage
            // and we show this message.
            m = e;
        }
        else {
            // Most of the modern API will carry 'code' and 'msg' in
            // T_ErrorMessage. We traslate it to the localize text
            // and show it. The 'msg' is the invalid part (usually
            // the field name) of the request.
            if (e.code && e.msg) {
                if (typeof e.msg === 'string')
                    ret = e.msg;
                m = this.errorMessage(e.code, e.msg);
            }
            else {
                // Some old API implementation return the 'code' and 'msg'
                // in a single string (which is carried by 'raw').
                // We split it and translate the message.
                let s = e.raw.split(':');
                if (s.length === 2) {
                    e.code = s[0];
                    e.msg = s[1];
                    ret = s[1];
                    m = this.errorMessage(s[0], s[1]);
                }
            }
            if (!m)
                m = `${EQI18N("H", "Data Error")} (${e.raw})`;
        }
        await nuError(m);
        return ret;
    }
    errorMessage(code, msg) {
        let r = null;
        if (this._errorMessage)
            r = this._errorMessage(code, msg);
        if (!r)
            r = this.onErrMsg(code, msg);
        if (!r && this._onErrMsg)
            r = this._onErrMsg(code, msg);
        if (!r) {
            if (msg === 'reqfilter')
                r = EQI18N("H", "Invalid Filter!");
            switch (code) {
                case 'EPERM':
                    r = `${EPERM_MSG} <br/>(${msg})`;
                    break;
                case 'EINVAL':
                    switch (msg) {
                        case 'name':
                            r = EQI18N("H", "Invalid Name!");
                            break;
                        case 'ipaddr':
                            r = EQI18N("H", "Invalid IP Address!");
                            break;
                        case 'macaddr':
                            r = EQI18N("H", "Invalid MAC Address!");
                            break;
                        case 'comment':
                            r = EQI18N("H", "Invalid Comment!");
                            break;
                        case 'email':
                            r = EQI18N("H", "Invalid Email Address!");
                            break;
                        case 'csv':
                            r = EQI18N("H", "Invalid data in the CSV file!");
                            break;
                        case 'csvfile':
                            r = EQI18N("H", "No file is selected.");
                            break;
                        default:
                            r = `${EQI18N("H", "Invalid Value!")} (${msg})`;
                            break;
                    }
                    break;
                case 'ENOENT':
                    r = `${EQI18N("H", "Not existing")} (${msg})`;
                    break;
                case 'EEXIST':
                    switch (msg) {
                        case 'name':
                            r = EQI18N("H", "The Name already exists!");
                            break;
                        case 'ipaddr':
                            r = EQI18N("H", "The IP Address already exists!");
                            break;
                        case 'macaddr':
                            r = EQI18N("H", "The MAC Address already exists!");
                            break;
                        default:
                            r = `${EQI18N("H", "Already exists")} (${msg})`;
                            break;
                    }
                    break;
            }
        }
        return r;
    }
    /**
     * This method call by errorMessage first. If sub-class need to customize the
     *   error message handling, it should be override this method.
     * @param _code error code
     * @param _msg error messages
     * @returns
     */
    onErrMsg(_code, _msg) { return null; }
}
export class wsDAO extends wsDAO_abstract {
    constructor(module, target, opt) {
        super(opt);
        this._module = module;
        this._target = target;
    }
    get daoName() { return `wsDAO(${this._module}.${this._target})`; }
    async getTotal() {
        let d = await this.request('COUNT');
        if (typeof d.payload === 'number')
            return d.payload;
        // d.payload should be a number.
        this.logger.error(`${this.$className}.getTotal(): invalid type of payload`, d);
        return -1;
    }
    request(action, payload, customs) {
        let dfd = $.Deferred(), _custom = $.extend(true, {}, this._customs, customs), req = {
            dao: { module: this._module, target: this._target, action: action },
            search: this.eqSearch.value,
            sfilter: this.eqSfilter.value,
            filter: this.eqFilter.value,
            sorter: this.eqSorter.value,
            page: this.eqPager.value,
            payload: payload || null
        };
        if (_custom)
            $.extend(true, req.dao, _custom);
        // Wait for WebSocket conection
        let wsock = webSock.Instance, _req = `${req.dao.module}.${req.dao.target}/${req.dao.action}`;
        this.logger.debug(`${this.$className}.request - ${_req}`, payload);
        $.when(wsock.connect())
            .fail(() => {
            this.logger.error(`${this.$className}.request - No WS connection`);
            dfd.reject('CONNERR', 'No WebSocket connection');
        })
            .done(() => {
            // Send the request
            $.when(wsock.request('DAO', req))
                .fail((r, e) => {
                this.logger.error(`${this.$className}.request - ${_req}, error:${r}`, e);
                dfd.reject(r, e);
            })
                .done(d => {
                this.logger.debug(`${this.$className}.response - ${_req}`, d);
                dfd.resolve(d);
            });
        });
        return dfd.promise();
    }
}
/**
 * EQAPI on WebSocket.
 *
 *	A common practice is to instantiate this class and use create(), read()
 *	update() and delete() methods for CRUD operations. Additional methods for
 *	import/export data are also available.
 *
 *	The basic error handling is implemented in handleError() and handleBADREQ()
 *	methods, and the option of onErrMsg callback function is used to extend
 *	the error message handling.
 */
export class wsEQAPI extends wsDAO_abstract {
    constructor(target, customs, opt) {
        super(opt);
        this._target = target;
        this._customs = customs || null;
        this._total = -1;
    }
    get daoName() { return `wsEQAPI(${this._target})`; }
    async getTotal() {
        if (this._total < 0) {
            this.logger.info(`${this.$className}.getTotal: no cache, issue a READ request`);
            let d = await this.request('READ');
            if (!d || !d.page || typeof d.page.total !== 'number') {
                this._total = 0;
                this.logger.error(`${this.$className}.getTotal: READ request failed`);
            }
        }
        return this._total;
    }
    request(action, payload, customs) {
        let _custom = $.extend(true, {}, this._customs, customs);
        if (action === 'EXPORT') {
            action = 'READ';
            // DON'T overwrite the specified export method
            if (!_custom['export'])
                _custom['export'] = 'FILEKEEPER';
        }
        else if (action === 'IMPORT') {
            action = 'UPDATE';
            // DON'T overwrite the specified import method
            if (!_custom['import'])
                _custom['import'] = 'FILEKEEPER';
        }
        let dfd = $.Deferred(), req = {
            eqapi: { target: this._target, action: action },
            payload: payload,
            custom: _custom
        };
        if (action === 'READ') {
            req['search'] = this.eqSearch.value;
            req['sfilter'] = this.eqSfilter.value;
            req['filter'] = this.eqFilter.value;
            req['sorter'] = this.eqSorter.value;
            req['page'] = this.eqPager.value;
        }
        if (_custom['filtermode']) {
            req['sfilter'] = this.eqSfilter.value;
            req['filter'] = this.eqFilter.value;
        }
        // Wait for WebSocket connection
        let wsock = webSock.Instance, _req = req.eqapi.target + '/' + req.eqapi.action;
        this.logger.debug(`${this.$className}.request - ${_req}`, req);
        $.when(wsock.connect())
            .fail(() => {
            this.logger.error(`${this.$className}.request - No WS connection`);
            dfd.reject('CONNERR', 'No WebSocket connection');
        })
            .done(() => {
            // Send the request
            $.when(wsock.request('EQAPI', req))
                .fail((r, e) => {
                this.logger.error(`${this.$className}.request - ${_req}, error:${r}`, e);
                if (r === 'INTERR' && e.payload)
                    this.logger.error(e.payload); // Usually backend error message.
                dfd.reject(r, e);
            })
                .done(d => {
                this.logger.debug(`${this.$className}.request - ${_req}`, d);
                dfd.resolve(d);
                // cache the total
                if (action === 'READ' && d && d.page && typeof d.page.total === 'number')
                    this._total = d.page.total;
            });
        });
        return dfd.promise();
    }
    // Alternaitve request methods
    read(payload, customs) { return this.request('READ', payload, customs); }
    export(payload, customs) { return this.request('EXPORT', payload, customs); }
    create(payload, customs) { return this.request('CREATE', payload, customs); }
    import(payload, customs) { return this.request('IMPORT', payload, customs); }
    update(payload, customs) { return this.request('UPDATE', payload, customs); }
    delete(payload, customs) { return this.request('DELETE', payload, customs); }
    /**
     * A simple EQAPI read function for Form-Based API.
     * @param name The name of the API
     * @param payload The payload for EQAPI read
     * @param customs The customs for EQAPI read
     * @returns Form-Based payload
     */
    static formRead(name, payload, customs) {
        let api = new wsEQAPI(name), dfd = $.Deferred();
        $.when(api.read(payload, customs))
            .fail((r, e) => {
            api.handleError(r, e);
            dfd.resolve(null);
        })
            .done((d) => {
            dfd.resolve(d.payload);
        });
        return dfd.promise();
    }
    /**
     * A simple EQAPI read function for Record-Based API.
     * @param name The name of the API
     * @param payload The payload for EQAPI read
     * @param customs The customs for EQAPI read
     * @returns Record-Based payload
     */
    static recRead(name, payload, customs) {
        let api = new wsEQAPI(name), dfd = $.Deferred();
        $.when(api.read(payload, customs))
            .fail((r, e) => {
            api.handleError(r, e);
            dfd.resolve(null);
        })
            .done((d) => {
            dfd.resolve(d.payload);
        });
        return dfd.promise();
    }
}
////////////////////////////////////////////////////////////
//
//	wsSUBS (WebSocket subscriber)
//
////////////////////////////////////////////////////////////
export class wsSUBS extends eqObject {
    constructor(module, target, cbfn) {
        super();
        ////////////////////////////////////////////////////////
        //
        //	Custom Requests manipulation
        //
        ////////////////////////////////////////////////////////
        this._customs = null;
        this._module = module;
        this._target = target;
        this._module_name = module ? `${module}.${target}` : `None.${target}`;
        this._reqdat = null;
        this._cbfn = cbfn;
    }
    get module_name() { return this._module_name; }
    get reqdata() { return this._reqdat; }
    notify(d) { return this._cbfn(d); }
    setCustom(name, value) {
        if (!this._customs)
            this._customs = { name: value };
        else
            this._customs[name] = value;
        return this;
    }
    setCustoms(req) {
        this._customs = req || null;
        return this;
    }
    ////////////////////////////////////////////////////////
    //
    //	Subscribe / Unsubscribe
    //
    ////////////////////////////////////////////////////////
    setPayload(payload) {
        this._payload = payload;
        return this;
    }
    async subscribe() {
        this.logger.debug(`Subscribing ${this.module_name}`);
        await this.request('SUBSCRIBE', this._payload);
    }
    async unsubscribe() {
        this.logger.debug(`Unsubscribing ${this.module_name}`);
        await this.request('UNSUBSCRIBE');
    }
    request(action, payload, customs) {
        let _custom = $.extend(true, {}, this._customs, customs), req = {
            subscribe: { module: this._module, target: this._target, action: action },
            payload: payload || null
        };
        if (_custom)
            $.extend(true, req.subscribe, _custom);
        const _req = `${req.subscribe.module}.${req.subscribe.target}/${req.subscribe.action}`;
        this.logger.debug(`${this.$className}.request - ${_req}`, payload);
        let wsock = webSock.Instance;
        this._reqdat = req;
        return action === 'SUBSCRIBE' ? wsock.subscribe(this) : wsock.unsubscribe(this);
    }
}
////////////////////////////////////////////////////////////
//
//	The timer
//
////////////////////////////////////////////////////////////
/**
 * Simple one-shot / periodic timer
 */
export class timer {
    constructor(periodic, msec, cbfn) {
        this._id = null;
        this._msec = msec;
        this._fn = cbfn;
        this._periodic = periodic;
    }
    /**
     * Call the callback function and schedule next.
     */
    timeout() {
        if (typeof this._fn === 'function') {
            if (this._fn() !== false && this._periodic && this._msec > 0)
                this.start();
        }
    }
    /**
     * Force timeout now.
     */
    fire() {
        this.stop();
        this.timeout();
    }
    /**
     * Start/Restart the timer.
     * @param msec timeout in milliseconds.
     * @param periodic Periodic timer or not.
     */
    start(msec, periodic) {
        this.stop();
        // Update time and periodic flag if specified.
        if (msec !== undefined)
            this._msec = msec;
        if (periodic !== undefined)
            this._periodic = periodic;
        if (this._msec <= 0)
            this.fire();
        else
            this._id = setTimeout(() => { this._id = null; this.timeout(); }, this._msec);
    }
    /**
     * Stop the timer
     */
    stop() {
        if (this._id !== null)
            clearTimeout(this._id);
        this._id = null;
    }
}
export class sessControl extends eqObject {
    /**
     * The current authorized username or NULL.
     */
    get username() { return this._auth ? this._auth.username : null; }
    /**
     * The role of the current authorized user or NULL.
     */
    get role() { return this._auth ? this._auth.role : null; }
    /**
     * Activate the session control by specifying
     * an activator, or NULL to deactivate.
     * @param activator An activator or NULL.
     */
    activate(activator) {
        this._activate = activator;
    }
    /**
     * Authorize this session and launch the
     * authentication process if needed.
     */
    async authorized() {
        if (this._activate) {
            let auth = await this._queryAuth(), now = getMyDateTime().getTimeStamp();
            this.logger.debug(`${this.$className}:auth =>`, auth);
            if (!auth || auth.validbefore < now)
                await this._login(auth);
            else
                this._auth = auth;
        }
    }
    /**
     * Logout this session and reload the page.
     */
    async logout() {
        if (this._activate) {
            let idp = this._auth?.idp;
            if (idp) {
                try {
                    // The SAML SLO will logout both the IDP
                    // and local sessions.
                    await nuYesno(IDP_LOGOUT);
                    window.location.href = `/cgi-bin/saml?slo=${idp}`;
                    return;
                }
                catch (e) {
                    // The user answer NO, logout local session only.
                    this.logger.info('Not logout from IDP.');
                }
            }
            await this._submitLogout();
            window.location.href = '/';
        }
    }
    /**
     * login for session timeout.
     */
    onTimeout() {
        if (this._activate) {
            if (this._auth)
                this._auth.validbefore = 0;
            this._login(this._auth);
        }
    }
    static get Instance() {
        if (!sessControl._instance)
            sessControl._instance = new sessControl();
        return sessControl._instance;
    }
    constructor() {
        super();
        ////////////////////////////////////////////////////////
        //
        //	The private implementation
        //
        ////////////////////////////////////////////////////////
        this._activate = null;
        // Register the user getter functions.
        registerUserInfoGetter(() => sessControl.Instance.username, () => sessControl.Instance.role);
    }
    /**
     * Show the login form for user authentication.
     * @param auth the current authentication status
     * @returns void
     */
    async _login(auth) {
        if (!this._activate)
            return;
        this.logger.info('[Session Control]: Authentication Required !');
        let reason = 'NOAUTH', user = null;
        if (auth && auth.lastupdate > 0 && auth.username) {
            let now = getMyDateTime().getTimeStamp();
            reason = auth.validbefore < now ? 'TIMEOUT' : null;
            user = auth.username;
        }
        while (reason) {
            this.logger.debug(`${this.$className}: (${user}) LOGIN due to (${reason})`);
            let cred = await this._activate.onCredentials(reason, user);
            if (!cred)
                break; // Login Cancelled
            this.logger.debug(`${this.$className}: Submit CRED =>`, cred);
            let ret = await this._submitLogin(cred);
            this.logger.debug(`${this.$className}: LOGIN =>`, ret);
            if (ret && ret.response) {
                let res = ret.response.result;
                switch (res) {
                    case 'BADPWD':
                        reason = 'BADPWD';
                        break;
                    case 'TIMEOUT':
                        reason = 'TIMEOUT';
                        break;
                    case 'GOOD':
                        this.freshLogin = reason !== 'TIMEOUT';
                        reason = null;
                        this._auth = this._toScAuthStatus(ret);
                        this._activate.onSignedIn(this._auth);
                        this.logger.info(`[Session Control]: (${cred.username}) signed-in =>`, this._auth);
                        if (user && user !== cred.username) {
                            this.logger.info(`User changed (${user} -> ${cred.username}), reload the page.`);
                            window.location.href = '/';
                        }
                        break;
                    case 'NOUSER':
                        reason = 'NOUSER';
                        user = null;
                        break;
                    default:
                        reason = 'NOAUTH';
                        user = null;
                        break;
                }
            }
        }
    }
    /**
     * Convert the sessauth API response to T_scAuthStatus.
     * @param d the response of sessauth API.
     */
    _toScAuthStatus(res) {
        if (!res || !res.payload) {
            this.logger.error(`${this.$className}: Invalid sessauth response =>`, res);
            throw 'Invalid sessauth response !!';
        }
        let d = res.payload, _lc = '', _ts = d.lastupdate || 0, _vb = _ts ? (_ts + d.expiration) : 0;
        if (d.preference && d.preference.langcode)
            _lc = d.preference.langcode;
        let ret = {
            sessid: d.sessid,
            lastupdate: _ts,
            validbefore: _vb,
            langcode: _lc,
            username: d.username,
            role: d.role
        };
        if (d.saml && d.idp) {
            ret.saml = d.saml;
            ret.idp = d.idp;
        }
        return ret;
    }
    ////////////////////////////////////////////////////////
    //
    //	The AJAX call to the sessauth APIs.
    //
    ////////////////////////////////////////////////////////
    /**
     * Query the authentication status of the current session.
     * @returns T_scAuthStatus or NULL.
     */
    _queryAuth() {
        let dfd = $.Deferred();
        $.ajax({
            url: '/cgi-bin/sessauth',
            type: 'GET',
            success: (data, _textStatus, _jqXHR) => {
                this.logger.debug(`${this.$className}:session status =>`, data);
                if (data.payload && data.response) {
                    if (!data.payload.username || !data.payload.role)
                        dfd.resolve(null);
                    else
                        dfd.resolve(this._toScAuthStatus(data));
                }
                else {
                    dfd.reject('Invalid response, missing payload or result!');
                }
            },
            error: (jqXHR, _textStatus, errorThrown) => {
                this.logger.error('sessauth error =>', errorThrown, jqXHR);
                dfd.reject(errorThrown);
            }
        });
        return dfd.promise();
    }
    /**
     * AJAX call to sessauth API for login operation.
     * @param cred the login credentials
     * @returns the response of sessauth API.
     */
    _submitLogin(cred) {
        let dfd = $.Deferred();
        $.ajax({
            url: '/cgi-bin/sessauth?op=login',
            type: 'POST',
            contentType: 'application/json; charset=utf-8',
            data: JSON.stringify({ username: cred.username, password: cred.password }),
            dataType: 'json',
            success: (data, _textStatus, _jqXHR) => { dfd.resolve(data); },
            error: (_jqXHR, _textStatus, _errorThrown) => { dfd.reject('CONNERR'); }
        });
        return dfd.promise();
    }
    _submitLogout() {
        let dfd = $.Deferred();
        $.ajax({
            url: '/cgi-bin/sessauth?op=logout',
            type: 'GET',
            success: (data, _textStatus, _jqXHR) => { dfd.resolve(data); },
            error: (_jqXHR, _textStatus, _errorThrown) => { dfd.reject('CONNERR'); }
        });
        return dfd.promise();
    }
}
